import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserRound } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tabipla/ui";
import { useAddGuest } from "../../../application/hooks/useGroups";
import type { GroupRole } from "@tabipla/shared";

interface AddGuestDialogProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddGuestDialog({ groupId, open, onOpenChange }: AddGuestDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [role, setRole] = useState<Exclude<GroupRole, "owner">>("editor");

  const addGuestMutation = useAddGuest(groupId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      await addGuestMutation.mutateAsync({ name, role });
      toast.success(t("groups.addGuestSuccess", "ゲストを追加しました"));
      onOpenChange(false);
      setName("");
      setRole("editor");
    } catch {
      toast.error(t("groups.addGuestError", "ゲストの追加に失敗しました"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-emerald-600" />
            {t("groups.addGuestTitle", "ゲストを追加")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "groups.addGuestDescription",
              "アカウントを持たないメンバーを名前だけで追加できます"
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("groups.guestName", "名前")}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("groups.guestNamePlaceholder", "ゲストの名前を入力")}
                required
                maxLength={50}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("groups.role")}</label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as Exclude<GroupRole, "owner">)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">{t("groups.roles.editor")}</SelectItem>
                  <SelectItem value="viewer">{t("groups.roles.viewer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={addGuestMutation.isPending || !name.trim()}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 sm:flex-none"
            >
              {addGuestMutation.isPending
                ? t("common.adding", "追加中...")
                : t("common.add", "追加")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
